import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useGetBoxPlotData } from '@restDataAccess';
import { MeasurementQueryRequest } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { DateTime, Duration } from 'luxon';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import Highcharts from '@/components/chart/highcharts';

import 'highcharts/esm/highcharts-more.js';

import { useEventClassUnitPair, useGetFriendlyProviderName } from '@/hooks';
import {
  fromApiDateTime,
  roundToTwoDecimals,
  toApiCalendarDay,
  toApiDate,
  toApiDateTime,
} from '@/utils';

import { AutoResizeHighChart } from '../../AutoResizeHighChart.tsx';
import { boxPlotBoxToMeasurementQuery } from '../../chartHelperFunctions/boxPlotBoxToMeasurementQuery.ts';
import { useChartOptionsWithLocale, useChartTimeRange, useSensorNames } from '../../hooks';
import {
  BoxPlotCategory,
  BoxPlotChartBoxConfigType,
  BoxPlotChartConfigType,
  ChartType,
  ICardDataState,
  intervalToDuration,
} from '../../types';
import {
  bgColor,
  boxPlotColor,
  dateTimeLabelFormats,
  defaultChartOptions,
  defaultLuxonChartDateFormat,
  defaultTooltipOptions,
  defaultXAxisOptions,
  defaultYAxisOptions,
  getLocaleFromLanguage,
  updateChartLocale,
} from '../defaultHighChartOptions.ts';

type BoxPlotChartData = {
  /** Epoch milliseconds for the datetime x-axis, only set for the day category. */
  x: number | undefined;
  low: number;
  q1: number;
  median: number;
  q3: number;
  high: number;
  name: string;
  /** Stable key matching `boxes` / measurements (set when building series data). */
  id: string;
};

const options = {
  ...defaultChartOptions,
  chart: {
    ...defaultChartOptions.chart,
    type: 'boxplot',
  },
  tooltip: {
    ...defaultTooltipOptions,
    headerFormat: undefined,
  },
};

interface BoxPlotHighChartProps {
  config: BoxPlotChartConfigType;
  setChartDataStat: (props: ICardDataState) => void;
  onChartNameChange?: (chartName: string | undefined) => void;
}

export const BoxPlotHighChart = ({
  config,
  setChartDataStat,
  onChartNameChange,
}: BoxPlotHighChartProps) => {
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);

  const { t, i18n } = useTranslation(['chartConfig']);
  const { getFriendlyProviderName } = useGetFriendlyProviderName();

  const { units } = useEventClassUnitPair();

  const sensorNames = useSensorNames();

  const getSensorName = useCallback(
    (sensorId: string) => sensorNames[sensorId] ?? sensorId,
    [sensorNames]
  );

  /**
   * Returns the name for a box plot box based on the chart category.
   * @param box The box plot box configuration.
   * @returns The name string for the box.
   * Remarks: ignore lint warning to keep clean code
   */
  const getBoxPlotName = useCallback(
    (box: BoxPlotChartBoxConfigType) => {
      const now = DateTime.now();
      const start =
        config.type === ChartType.LIVE
          ? now.minus(Duration.fromObject(intervalToDuration[config.interval]))
          : (box.start ?? now);
      const end = config.type === ChartType.LIVE ? now : (box.end ?? now);

      switch (config.category) {
        case BoxPlotCategory.Sensor:
          return t('chartConfig:dateRangeTooltip', {
            item: getFriendlyProviderName(box.provider),
            start: start.toJSDate().toLocaleDateString(),
            end: end.toJSDate().toLocaleDateString(),
          });
        case BoxPlotCategory.Provider:
          return t('chartConfig:dateRangeTooltip', {
            item: getSensorName(box.sensorId),
            start: start.toJSDate().toLocaleDateString(),
            end: end.toJSDate().toLocaleDateString(),
          });
        case BoxPlotCategory.Day:
          return `${getSensorName(box.sensorId)} ${getFriendlyProviderName(box.provider)}`;
      }
    },
    [config.category, config.interval, config.type, getFriendlyProviderName, getSensorName, t]
  );

  const getBoxLabel = useCallback(
    (box: MeasurementQueryRequest) => {
      switch (config.category) {
        case BoxPlotCategory.Day: {
          const locale = getLocaleFromLanguage(i18n.language);
          return new Intl.DateTimeFormat(locale, defaultLuxonChartDateFormat).format(
            fromApiDateTime(box.start).toJSDate()
          );
        }
        case BoxPlotCategory.Sensor:
          return getSensorName(box.inventoryId);
        case BoxPlotCategory.Provider:
          return getFriendlyProviderName(box.provider);
      }
    },
    [config.category, getFriendlyProviderName, getSensorName, i18n.language]
  );

  const getBoxUnit = useCallback(
    (box: MeasurementQueryRequest) => units[box.provider] ?? '',
    [units]
  );

  const { start, end, updateInterval } = useChartTimeRange(config.type, config.interval);

  const boxes = useMemo(() => {
    // All start and end dates specified by boxes
    if (config.type === ChartType.HISTORICAL) {
      return config.boxes.reduce(
        (acc, box) => ({
          ...acc,
          [uuidv4()]: boxPlotBoxToMeasurementQuery(box, {
            start: toApiDateTime(box.start ?? DateTime.now()),
            end: toApiDateTime(box.end ?? DateTime.now()),
          }),
        }),
        {} as Record<string, MeasurementQueryRequest>
      );
    }
    // One box per day for the length of "interval"
    if (config.category === BoxPlotCategory.Day) {
      const duration = Duration.fromObject(intervalToDuration[config.interval]).shiftTo('days');
      const now = end ? end.startOf('day') : toApiCalendarDay(DateTime.now());
      const box = config.boxes[0] ?? { sensorId: '', provider: '' };
      const requests: Record<string, MeasurementQueryRequest> = {};
      for (let i = 0; i < duration.days; i++) {
        const dayEnd = now.minus(Duration.fromObject({ days: i }));
        const dayStart = dayEnd.minus(Duration.fromObject({ days: 1 }));
        requests[uuidv4()] = boxPlotBoxToMeasurementQuery(box, {
          start: toApiDate(dayStart),
          end: toApiDate(dayEnd),
        });
      }
      return requests;
    }
    // One box per sensor/provider, start and end date decided by interval
    return config.boxes.reduce(
      (acc, box) => ({
        ...acc,
        [uuidv4()]: boxPlotBoxToMeasurementQuery(box, {
          start: toApiDateTime(start ?? DateTime.now()),
          end: toApiDateTime(end ?? DateTime.now()),
        }),
      }),
      {} as Record<string, MeasurementQueryRequest>
    );
  }, [config.boxes, config.category, config.interval, config.type, start, end]);

  //**** Getting historical data ****//
  const {
    data: measurements,
    isLoading,
    isError,
    isFetched,
    isRateLimited,
  } = useGetBoxPlotData(boxes, updateInterval.toMillis());

  useEffect(() => {
    setChartDataStat({
      isLoading: isLoading,
      isError: isError,
      isEmpty: isFetched && !measurements,
      isRateLimited: isRateLimited,
    });
  }, [isError, isFetched, isLoading, isRateLimited, measurements, setChartDataStat]);

  /**
   * The tooltip formatter a box plot.
   * @returns The tooltip formatter for the boxplot.
   * Remarks: ignore lint warning to keep clean code
   */
  // eslint-disable-next-line react-x/no-unnecessary-use-memo
  const tooltipFormatter = useMemo(
    () =>
      function (this: Highcharts.Point) {
        const values = (this.options ?? {}) as BoxPlotChartData;

        const box = boxes[values.id];
        const boxMeasurements = measurements?.[values.id]?.[box.inventoryId];
        const label = getBoxLabel(box);
        const unit = getBoxUnit(box);

        return `${label}<br/>
              ${t('boxPlot.statistics.max')}: <strong>${values.high !== undefined ? roundToTwoDecimals(values.high) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.q3')}: <strong>${values.q3 !== undefined ? roundToTwoDecimals(values.q3) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.median')}: <strong>${values.median !== undefined ? roundToTwoDecimals(values.median) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.q1')}: <strong>${values.q1 !== undefined ? roundToTwoDecimals(values.q1) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.min')}: <strong>${values.low !== undefined ? roundToTwoDecimals(values.low) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.mean')}: <strong>${boxMeasurements?.mean !== undefined ? roundToTwoDecimals(boxMeasurements?.mean) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.standardDeviation')}: <strong>${boxMeasurements?.standardDeviation !== undefined ? roundToTwoDecimals(boxMeasurements?.standardDeviation) : ''}</strong> ${unit}<br>
              ${t('boxPlot.statistics.iqr')}: <strong>${boxMeasurements?.iqr !== undefined ? roundToTwoDecimals(boxMeasurements?.iqr) : ''}</strong> ${unit}<br>`;
      },
    [boxes, getBoxLabel, getBoxUnit, measurements, t]
  );

  // Update chart name in parent component
  useEffect(() => {
    const chartName = config.boxes[0] ? getBoxPlotName(config.boxes[0]) : undefined;
    onChartNameChange?.(chartName);
  }, [config.boxes, getBoxPlotName, onChartNameChange]);

  //**** Set chart options ****//
  useEffect(() => {
    // Defined separately to avoid triggering useEffect unnecessarily when used in its dependency array.
    const chart = highChartsRef.current?.chart;

    while (chart?.series.length) {
      chart.series[0]?.remove();
    }

    const locale = getLocaleFromLanguage(i18n.language);

    // Set locale both globally and on the chart instance to ensure it's applied
    // This is critical because existing chart instances may not pick up global setOptions changes
    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    // Update the chart instance's lang property directly to ensure locale is applied
    updateChartLocale(chart, locale);

    chart?.xAxis?.[0].update({
      ...defaultXAxisOptions,
      type: config.category === BoxPlotCategory.Day ? 'datetime' : 'category',
      ...(config.category === BoxPlotCategory.Day && { dateTimeLabelFormats }),
    });

    const referenceBox = config.boxes[0];

    chart?.yAxis[0]?.update({
      ...defaultYAxisOptions,
      id: referenceBox.provider,
      title: {
        ...defaultYAxisOptions.title,
        text:
          config.category !== BoxPlotCategory.Provider || config.boxes.length === 1
            ? units[referenceBox.provider]
            : undefined,
      },
    });

    const chartData: Record<string, BoxPlotChartData> = measurements
      ? Object.entries(measurements).reduce((acc, [key, dataDict]) => {
          const box = boxes[key];
          const data = box?.inventoryId ? dataDict[box.inventoryId] : undefined;
          if (!data) return acc;
          return {
            ...acc,
            [key]: {
              ...data,
              low: data.lowerWhisker,
              high: data.upperWhisker,
              x:
                config.category === BoxPlotCategory.Day
                  ? DateTime.fromISO(box.start).toMillis()
                  : undefined,
              name: getBoxLabel(box),
              id: key,
            },
          };
        }, {})
      : {};

    const data = Object.values(chartData);
    if (config.category === BoxPlotCategory.Day)
      data.sort((a, b) => (a.x !== undefined && b.x !== undefined ? a.x - b.x : 0));

    chart?.addSeries({
      yAxis: referenceBox.provider,
      id: referenceBox.provider,
      name: getBoxPlotName(referenceBox),
      showInLegend: false,
      type: 'boxplot',
      data: data,
      color: boxPlotColor,
      fillColor: bgColor,
      tooltip: {
        ...defaultTooltipOptions,
        headerFormat: '',
        pointFormatter: tooltipFormatter,
      },
    });
  }, [
    i18n,
    i18n.language,
    boxes,
    config.boxes,
    config.category,
    getBoxLabel,
    getBoxPlotName,
    measurements,
    tooltipFormatter,
    units,
  ]);

  //**** Rendering ****//
  const chartOptionsWithLocale = useChartOptionsWithLocale(options);

  return (
    <AutoResizeHighChart
      highcharts={Highcharts}
      options={chartOptionsWithLocale}
      highChartsRef={highChartsRef}
    />
  );
};
